import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Store, CreditCard, Banknote, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const links = [
    { to: "/admin", icon: LayoutDashboard, label: t("nav.overview"), end: true, id: "overview" },
    { to: "/admin/shops", icon: Store, label: t("nav.shops"), id: "shops" },
    { to: "/admin/payments", icon: Banknote, label: t("nav.payments"), id: "payments" },
    { to: "/admin/plans", icon: CreditCard, label: t("nav.plans"), id: "plans" },
  ];

  return (
    <div className="min-h-screen flex bg-background" data-testid="admin-layout">
      <aside className="w-60 shrink-0 bg-[#0F172A] text-slate-200 hidden lg:flex flex-col fixed inset-y-0 z-30">
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-slate-700/60">
          <ShieldCheck className="h-7 w-7 text-amber-500" />
          <div>
            <div className="font-heading font-bold text-white leading-tight">{t("brand")}</div>
            <div className="text-xs text-slate-400">Super Admin</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-amber-600 text-white" : "text-slate-300 hover:bg-slate-700/60 hover:text-white"
                }`
              }
            >
              <l.icon className="h-[18px] w-[18px]" />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-slate-700/60 hover:text-white transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" />
          {t("nav.logout")}
        </button>
      </aside>
      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 glass-topbar border-b border-slate-200 px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-wider text-slate-500">Command Center</div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="text-sm text-slate-600" data-testid="user-email-label">{user?.email}</div>
          </div>
        </header>
        <nav className="lg:hidden sticky top-16 z-20 bg-[#0F172A] px-3 py-2 flex gap-1 overflow-x-auto" data-testid="mobile-admin-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 ${
                  isActive ? "bg-amber-600 text-white" : "text-slate-300"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          ))}
          <button onClick={async () => { await logout(); navigate("/login"); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 text-slate-300">
            <LogOut className="h-4 w-4" />{t("nav.logout")}
          </button>
        </nav>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
